import type { DialogProps } from "@mui/material/Dialog";
import type { DrawerProps } from "@mui/material/Drawer";
import type { PaperProps } from "@mui/material/Paper";
import type { SnackbarProps } from "@mui/material/Snackbar";

export interface OverlaySize {
	width: number;
	height: number;
}

export type GhostPaperProps = Pick<
	PaperProps,
	"className" | "elevation" | "square" | "style" | "sx" | "variant"
>;

export type GhostDialogProps = Pick<
	DialogProps,
	| "className"
	| "fullScreen"
	| "fullWidth"
	| "hideBackdrop"
	| "maxWidth"
	| "scroll"
	| "sx"
	| "TransitionComponent"
	| "transitionDuration"
> & { PaperProps?: GhostPaperProps };

export type GhostDrawerProps = Pick<
	DrawerProps,
	| "anchor"
	| "className"
	| "elevation"
	| "hideBackdrop"
	| "sx"
	| "transitionDuration"
> & { PaperProps?: GhostPaperProps };

export type GhostSnackbarProps = Pick<
	SnackbarProps,
	| "anchorOrigin"
	| "className"
	| "sx"
	| "TransitionComponent"
	| "transitionDuration"
>;

/** A snackbar's surface is its caller's content root; its class names restyle the ghost. */
export interface GhostSnackbarSurface {
	className: string;
}

export type OverlayDeparture =
	| { kind: "dialog"; props: GhostDialogProps; size: OverlaySize }
	| { kind: "drawer"; props: GhostDrawerProps; size: OverlaySize }
	| {
			kind: "snackbar";
			props: GhostSnackbarProps;
			size: OverlaySize;
			surface: GhostSnackbarSurface;
	  };

export type OverlayKind = OverlayDeparture["kind"];

/**
 * Modal overlays (dialogs and drawers) replace each other, e.g. a loading dialog swapped
 * for the loaded drawer; a snackbar opening never replaces one, and vice versa.
 */
type OverlayLayer = "modal" | "snackbar";
const getLayer = (kind: OverlayKind): OverlayLayer =>
	kind === "snackbar" ? "snackbar" : "modal";

export type OverlayGhost = OverlayDeparture & { key: number };

/** `sync` is true when the listener must render before the browser paints. */
type Listener = (sync: boolean) => void;

let nextKey = 0;
let ghosts: readonly OverlayGhost[] = [];
let pendingDepartures: OverlayDeparture[] = [];
let openedLayersThisTick = new Set<OverlayLayer>();
let isFlushScheduled = false;
const listeners = new Set<Listener>();

const emit = (sync: boolean) => listeners.forEach((listener) => listener(sync));

/**
 * Departures and openings reported during one React commit are resolved together.
 * An overlay that is replaced by another one of its layer in the same commit (e.g. a
 * loading dialog swapped for the loaded dialog) must not leave a ghost on top of its successor.
 */
const flush = () => {
	isFlushScheduled = false;
	const openedLayers = openedLayersThisTick;
	const departures = pendingDepartures.filter(
		(departure) => !openedLayers.has(getLayer(departure.kind)),
	);
	pendingDepartures = [];
	openedLayersThisTick = new Set();

	if (departures.length === 0 || listeners.size === 0) return;

	ghosts = [
		...ghosts,
		...departures.map((departure) => ({ ...departure, key: ++nextKey })),
	];
	// The real overlay is already gone: its ghost must be painted in the same frame.
	emit(true);
};

const scheduleFlush = () => {
	if (isFlushScheduled) return;
	isFlushScheduled = true;
	queueMicrotask(flush);
};

export const overlayGhostStore = {
	subscribe(listener: Listener) {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	},
	getGhosts: () => ghosts,
	reportOpen(kind: OverlayKind) {
		openedLayersThisTick.add(getLayer(kind));
		scheduleFlush();
	},
	reportDeparture(departure: OverlayDeparture) {
		pendingDepartures.push(departure);
		scheduleFlush();
	},
	remove(key: number) {
		ghosts = ghosts.filter((ghost) => ghost.key !== key);
		emit(false);
	},
};
